import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Category as CategoryIcon } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { categoriesAPI } from "../services/api.js";
import { useAuth } from "../contexts/AuthContext";

const CategoryDialog = ({ open, onClose, editingCategory, categories }) => {
  const [formData, setFormData] = useState({
    name: editingCategory?.name || "",
    description: editingCategory?.description || "",
    parent_id: editingCategory?.parent_id || "",
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: categoriesAPI.createCategory,
    onSuccess: () => {
      enqueueSnackbar("Category created successfully", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["categories-tree"] });
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to create category",
        { variant: "error" }
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) =>
      categoriesAPI.updateCategory(editingCategory.id, data),
    onSuccess: () => {
      enqueueSnackbar("Category updated successfully", {
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["categories-tree"] });
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to update category",
        { variant: "error" }
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      enqueueSnackbar("Category name is required", { variant: "error" });
      return;
    }

    const submitData = {
      ...formData,
      parent_id: formData.parent_id || null,
    };

    if (editingCategory) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  React.useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name || "",
        description: editingCategory.description || "",
        parent_id: editingCategory.parent_id || "",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        parent_id: "",
      });
    }
  }, [editingCategory, open]);

  const isLoading = updateMutation.isLoading; // Single database mode - no creation

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingCategory ? "Edit Category" : "Create New Category"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={isLoading}
          />

          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            disabled={isLoading}
          />

          <TextField
            select
            margin="dense"
            label="Parent Category"
            fullWidth
            value={formData.parent_id}
            onChange={(e) => handleInputChange("parent_id", e.target.value)}
            helperText="Leave empty for root category"
            disabled={isLoading}
          >
            <MenuItem value="">None (Root Category)</MenuItem>
            {categories
              .filter(
                (cat) => !editingCategory || cat.id !== editingCategory.id
              )
              .map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.full_path}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {editingCategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Database dialog removed

const CategoryTreeItem = ({
  category,
  onEdit,
  onDelete,
  canDelete,
  isAdminUser,
}) => {
  return (
    <TreeItem
      itemId={category.id.toString()}
      label={
        <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
          <CategoryIcon sx={{ mr: 1, fontSize: 20 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              {category.name}
            </Typography>
            {category.description && (
              <Typography variant="caption" color="text.secondary">
                {category.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ ml: 2 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(category);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            {canDelete && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(category);
                }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      }
    >
      {category.children?.map((child) => (
        <CategoryTreeItem
          key={child.id}
          category={child}
          onEdit={onEdit}
          onDelete={onDelete}
          canDelete={isAdminUser && !child.children?.length}
          isAdminUser={isAdminUser}
        />
      ))}
    </TreeItem>
  );
};

const Categories = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  // Fetch category tree
  const {
    data: categoriesTreeData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useQuery({
    queryKey: ["categories-tree"],
    queryFn: categoriesAPI.getCategoryTree,
  });

  // Fetch flat categories for parent selection
  const {
    data: flatCategoriesData,
    isLoading: flatCategoriesLoading,
    error: flatCategoriesError,
  } = useQuery({
    queryKey: ["categories-flat"],
    queryFn: () => categoriesAPI.getCategories({ include_children: false }),
  });

  // Databases query removed

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: categoriesAPI.deleteCategory,
    onSuccess: () => {
      enqueueSnackbar("Category deleted successfully", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["categories-tree"] });
      queryClient.invalidateQueries({ queryKey: ["categories-flat"] });
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to delete category",
        { variant: "error" }
      );
    },
  });

  // Database actions removed

  const handleCreateNew = () => {
    setEditingCategory(null);
    setDialogOpen(true);
  };

  const handleEdit = (category) => {
    setEditingCategory({ ...category });
    setDialogOpen(true);
  };

  const handleDelete = (category) => {
    setDeleteDialog({ open: true, item: category });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.item) {
      deleteMutation.mutate(deleteDialog.item.id);
    }
    setDeleteDialog({ open: false, item: null });
  };

  const handleCancelDelete = () => setDeleteDialog({ open: false, item: null });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  // Database handlers removed

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const categoryTree = categoriesTreeData?.data?.category_tree || [];
  const flatCategories = flatCategoriesData?.data?.categories || [];
  const databases = [];

  if (categoriesLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">Categories</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew}>Create Category</Button>
      </Box>

      <Typography variant="body1" color="text.secondary" mb={3}>Organize your uploads with hierarchical categories</Typography>

      {/* Databases tab removed */}

      {/* Error Alerts */}
      {categoriesError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load categories: {categoriesError?.response?.data?.error || categoriesError?.message}
        </Alert>
      )}

      {/* Categories Content */}
      <Card>
        <CardContent>
          {categoryTree.length > 0 ? (
            <SimpleTreeView sx={{ flexGrow: 1, overflowY: "auto" }}>
              {categoryTree.map((category) => (
                <CategoryTreeItem
                  key={category.id}
                  category={category}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  canDelete={isAdmin() && !category.children?.length}
                  isAdminUser={isAdmin()}
                />
              ))}
            </SimpleTreeView>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CategoryIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Categories Found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create your first category to start organizing your uploads
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
              >
                Create First Category
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography gutterBottom>
            Are you sure you want to delete
            {" "}
            <strong>
              "{deleteDialog.item ? deleteDialog.item.name : ""}"
            </strong>
            ?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2">
              Delete the category and all its subcategories (if any)
            </Typography>
            <Typography component="li" variant="body2">
              Deletion will be blocked if the category or any subcategory has
              uploads
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={deleteMutation.isLoading ? <CircularProgress size={16} /> : null}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs */}
      <CategoryDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editingCategory={editingCategory}
        categories={flatCategories}
      />

      {/* Database dialog removed */}
    </Box>
  );
};

export default Categories;
